/**
 * 2026-05-25 — local-only demo community chat used in the
 * experimental crypto-price-style market view to iterate on the
 * comments UX with realistic populated data. All data is hardcoded
 * fixtures — no API calls. Replace with <CommentsSection/> once
 * the UX is locked in.
 */
"use client";

import { useState } from "react";
import {
  ChevronUp,
  ChevronDown,
  MessageCircle,
  MoreHorizontal,
  Flame,
  Wallet,
  Check,
} from "lucide-react";

type Sort = "trending" | "new" | "top";

interface DemoMessage {
  id: string;
  author: { name: string; handle: string; avatarColor: string; verified?: boolean };
  body: string;
  position?: "YES" | "NO" | null;
  positionCents?: number;
  postedMinutesAgo: number;
  score: number;
  myVote?: 1 | -1 | 0;
  replies?: DemoReply[];
}

interface DemoReply {
  id: string;
  author: { name: string; handle: string; avatarColor: string };
  body: string;
  postedMinutesAgo: number;
  score: number;
}

const MESSAGES: DemoMessage[] = [
  {
    id: "m1",
    author: { name: "Maya R.", handle: "macroflow", avatarColor: "#2d3b2e", verified: true },
    body: "ETF inflows ran 4× BTC issuance through May. Even if half of that pace holds into Q4, supply shock alone gets you north of 180K. 200K isn't a stretch — it's a floor.",
    position: "YES",
    positionCents: 30,
    postedMinutesAgo: 14,
    score: 47,
    myVote: 1,
    replies: [
      {
        id: "m1-r1",
        author: { name: "Devin Q.", handle: "spotbias", avatarColor: "#b8924f" },
        body: "Flows trail price by ~2 weeks. We could absolutely round-trip the whole year.",
        postedMinutesAgo: 9,
        score: 12,
      },
      {
        id: "m1-r2",
        author: { name: "Maya R.", handle: "macroflow", avatarColor: "#2d3b2e" },
        body: "Sure, but the bid is structurally different now. RIAs aren't fast money.",
        postedMinutesAgo: 6,
        score: 18,
      },
    ],
  },
  {
    id: "m2",
    author: { name: "Theo K.", handle: "shortvol", avatarColor: "#7a4a4a" },
    body: "30¢ is generous. Half of 2026 left, macro tightening risk underpriced, and we still haven't seen the post-ETF cooling. Sitting on NO.",
    position: "NO",
    positionCents: 70,
    postedMinutesAgo: 28,
    score: 23,
  },
  {
    id: "m3",
    author: { name: "Anjali P.", handle: "deltahedger", avatarColor: "#4a5d6c" },
    body: "What's the implied vol on options expiring Dec 31? If realized < implied, this market is mispriced.",
    postedMinutesAgo: 41,
    score: 9,
    replies: [
      {
        id: "m3-r1",
        author: { name: "kbot", handle: "kbot", avatarColor: "#3a3a3a" },
        body: "Deribit Dec 26 80K-260K straddle at 62 IV last I checked. Tight.",
        postedMinutesAgo: 35,
        score: 14,
      },
    ],
  },
  {
    id: "m4",
    author: { name: "Jordan T.", handle: "fadetheherd", avatarColor: "#5a4a7a" },
    body: "Everyone in this thread is anchored to the spot price. The question is the right tail probability. Even a 50% chance of one parabolic month gets you there.",
    position: "YES",
    positionCents: 32,
    postedMinutesAgo: 55,
    score: 31,
  },
  {
    id: "m5",
    author: { name: "Lena S.", handle: "lenas", avatarColor: "#c08855" },
    body: "Reminder to size positions. Don't go all-in on a binary that resolves in 7 months.",
    postedMinutesAgo: 78,
    score: 56,
  },
  {
    id: "m6",
    author: { name: "Rui M.", handle: "ondechain", avatarColor: "#2f6f4f", verified: true },
    body: "Long-term holder supply at ATH. Coins moved < 1y now < 30% of float. Mechanically bullish.",
    position: "YES",
    positionCents: 28,
    postedMinutesAgo: 96,
    score: 38,
    replies: [
      {
        id: "m6-r1",
        author: { name: "Theo K.", handle: "shortvol", avatarColor: "#7a4a4a" },
        body: "LTH supply was at ATH in Aug 2022 too. Then we tagged 16K.",
        postedMinutesAgo: 92,
        score: 11,
      },
    ],
  },
  {
    id: "m7",
    author: { name: "kbot", handle: "kbot", avatarColor: "#3a3a3a" },
    body: "Anyone else notice the liquidity at 35¢ and 42¢? Looks like someone's laddering buys.",
    postedMinutesAgo: 122,
    score: 7,
  },
  {
    id: "m8",
    author: { name: "Sasha L.", handle: "sashalewis", avatarColor: "#5d3d6d" },
    body: "Bought a small YES at 26¢ last week. Will add on any dip below 25¢.",
    position: "YES",
    positionCents: 26,
    postedMinutesAgo: 180,
    score: 19,
  },
];

function relativeTime(minutes: number): string {
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
}

export function DemoCommunityChat() {
  const [sort, setSort] = useState<Sort>("trending");
  const [onlyHolders, setOnlyHolders] = useState(false);

  // "Has an open order" proxy in the demo: the message author posted
  // with an explicit YES/NO position. Real wire-up would resolve from
  // /v1/markets/:id/positions and join by user_id.
  const filtered = onlyHolders
    ? MESSAGES.filter((m) => m.position != null)
    : MESSAGES;

  const sorted = [...filtered].sort((a, b) => {
    if (sort === "top") return b.score - a.score;
    if (sort === "new") return a.postedMinutesAgo - b.postedMinutesAgo;
    // Trending — score weighted down by age. Simple decay; the real
    // backend would use a /v1/comments?sort=trending endpoint.
    const aTrend = a.score / Math.pow(a.postedMinutesAgo + 30, 0.6);
    const bTrend = b.score / Math.pow(b.postedMinutesAgo + 30, 0.6);
    return bTrend - aTrend;
  });

  return (
    <div className="text-[14px]" style={{ color: "#0A0B0D" }}>
      {/* Header row — count + sort tabs + holders filter. Wraps to a
          second line on small viewports. */}
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-[20px] font-semibold tracking-[-0.01em]">
          Discussion
          <span className="ml-2 text-[14px] font-normal" style={{ color: "#5B616E" }}>
            {sorted.length}
            {onlyHolders && MESSAGES.length !== sorted.length && (
              <span style={{ color: "#8A919E" }}> of {MESSAGES.length}</span>
            )}
          </span>
        </h2>
        <div className="flex flex-wrap items-center gap-2">
          <div
            className="flex items-center gap-1 rounded-full p-1"
            style={{ background: "#F7F8FA" }}
          >
            <SortPill
              active={sort === "trending"}
              onClick={() => setSort("trending")}
              label="Trending"
              icon={<Flame size={11} strokeWidth={2} />}
            />
            <SortPill
              active={sort === "new"}
              onClick={() => setSort("new")}
              label="New"
            />
            <SortPill
              active={sort === "top"}
              onClick={() => setSort("top")}
              label="Top"
            />
          </div>
          <button
            type="button"
            onClick={() => setOnlyHolders((v) => !v)}
            aria-pressed={onlyHolders}
            className="inline-flex min-h-9 items-center gap-1.5 rounded-full px-3 text-[12px] font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2d3b2e]/30"
            style={{
              background: onlyHolders
                ? "rgba(45, 59, 46, 0.08)"
                : "transparent",
              color: onlyHolders ? "#2d3b2e" : "#5B616E",
            }}
            onMouseEnter={(e) => {
              if (!onlyHolders) e.currentTarget.style.background = "#F2F3F5";
            }}
            onMouseLeave={(e) => {
              if (!onlyHolders) e.currentTarget.style.background = "transparent";
            }}
            title={
              onlyHolders
                ? "Showing only authors with an open YES/NO position"
                : "Filter to authors with an open YES/NO position"
            }
          >
            <Wallet size={12} strokeWidth={2} aria-hidden="true" />
            <span className="hidden sm:inline">Has position</span>
            <span className="sm:hidden">Holders</span>
          </button>
        </div>
      </div>

      {/* Composer (visual only — non-interactive demo) */}
      <div
        className="mb-6 flex gap-3 rounded-2xl p-3"
        style={{ background: "#F7F8FA" }}
      >
        <Avatar name="You" color="#0A0B0D" />
        <div className="flex-1">
          <textarea
            rows={2}
            placeholder="Share your take…"
            className="w-full resize-none bg-transparent text-[14px] leading-[1.5] outline-none placeholder:text-[#8A919E]"
            style={{ color: "#0A0B0D" }}
            aria-label="Share your take"
          />
          <div className="mt-1 flex items-center justify-between">
            <span className="text-[11px]" style={{ color: "#8A919E" }}>
              Tip: tag a market with $ticker
            </span>
            <button
              type="button"
              className="rounded-full px-3.5 py-1.5 text-[12px] font-semibold"
              style={{ background: "#0A0B0D", color: "#FFFFFF" }}
            >
              Post
            </button>
          </div>
        </div>
      </div>

      {/* Thread */}
      <ul className="flex flex-col gap-5">
        {sorted.map((m) => (
          <li key={m.id}>
            <MessageRow message={m} />
            {m.replies && m.replies.length > 0 && (
              <ul
                className="ml-11 mt-3 flex flex-col gap-3 border-l pl-4"
                style={{ borderColor: "#ECEFF2" }}
              >
                {m.replies.map((r) => (
                  <li key={r.id}>
                    <ReplyRow reply={r} />
                  </li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function SortPill({
  active,
  onClick,
  label,
  icon,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  icon?: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-[12px] font-medium transition-colors"
      style={{
        background: active ? "#FFFFFF" : "transparent",
        color: active ? "#0A0B0D" : "#5B616E",
        boxShadow: active ? "0 1px 2px rgba(15,23,42,0.06)" : "none",
      }}
    >
      {icon}
      {label}
    </button>
  );
}

function Avatar({ name, color, size = 36 }: { name: string; color: string; size?: number }) {
  const initial = name.slice(0, 1).toUpperCase();
  return (
    <div
      className="flex flex-shrink-0 items-center justify-center rounded-full text-[13px] font-semibold text-white"
      style={{ background: color, width: size, height: size }}
      aria-hidden="true"
    >
      {initial}
    </div>
  );
}

function PositionChip({ position, cents }: { position: "YES" | "NO"; cents: number }) {
  const isYes = position === "YES";
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full px-2 py-[2px] text-[10px] font-semibold uppercase tracking-[0.04em]"
      style={{
        background: isYes ? "rgba(1,160,74,0.10)" : "rgba(207,32,47,0.10)",
        color: isYes ? "#01A04A" : "#CF202F",
      }}
    >
      <span>{position}</span>
      <span className="tabular-nums">{cents}¢</span>
    </span>
  );
}

function VoteStack({ score, myVote }: { score: number; myVote?: 1 | -1 | 0 }) {
  const [vote, setVote] = useState<1 | -1 | 0>(myVote ?? 0);
  const display = score + (vote - (myVote ?? 0));
  return (
    <div className="mt-2 inline-flex items-center gap-0.5">
      <button
        type="button"
        onClick={() => setVote(vote === 1 ? 0 : 1)}
        className="rounded-md p-1 transition-colors hover:bg-[#F7F8FA]"
        aria-label="Upvote"
      >
        <ChevronUp
          size={16}
          strokeWidth={1.75}
          style={{ color: vote === 1 ? "#01A04A" : "#8A919E" }}
        />
      </button>
      <span
        className="min-w-[18px] text-center text-[12px] font-semibold tabular-nums"
        style={{ color: "#0A0B0D" }}
      >
        {display}
      </span>
      <button
        type="button"
        onClick={() => setVote(vote === -1 ? 0 : -1)}
        className="rounded-md p-1 transition-colors hover:bg-[#F7F8FA]"
        aria-label="Downvote"
      >
        <ChevronDown
          size={16}
          strokeWidth={1.75}
          style={{ color: vote === -1 ? "#CF202F" : "#8A919E" }}
        />
      </button>
    </div>
  );
}

function MessageRow({ message }: { message: DemoMessage }) {
  return (
    <div className="flex gap-3">
      <Avatar name={message.author.name} color={message.author.avatarColor} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[13px]">
          <span className="font-semibold" style={{ color: "#0A0B0D" }}>
            {message.author.name}
          </span>
          {message.author.verified && (
            <span
              className="text-[10px]"
              style={{ color: "#1652F0" }}
              title="Verified"
            >
              ✓
            </span>
          )}
          <span style={{ color: "#8A919E" }}>@{message.author.handle}</span>
          <span style={{ color: "#8A919E" }}>·</span>
          <span style={{ color: "#8A919E" }}>{relativeTime(message.postedMinutesAgo)}</span>
          {message.position && message.positionCents != null && (
            <span className="ml-1">
              <PositionChip position={message.position} cents={message.positionCents} />
            </span>
          )}
        </div>
        <p
          className="mt-1.5 whitespace-pre-line text-[14px] leading-[1.5]"
          style={{ color: "#0A0B0D" }}
        >
          {message.body}
        </p>
        <div className="flex items-center gap-3">
          <VoteStack score={message.score} myVote={message.myVote} />
          <button
            type="button"
            className="mt-2 inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-[12px] font-medium transition-colors hover:bg-[#F7F8FA]"
            style={{ color: "#5B616E" }}
          >
            <MessageCircle size={14} strokeWidth={1.75} />
            Reply
          </button>
          <button
            type="button"
            className="mt-2 rounded-md p-1 transition-colors hover:bg-[#F7F8FA]"
            style={{ color: "#8A919E" }}
            aria-label="More"
          >
            <MoreHorizontal size={14} strokeWidth={1.75} />
          </button>
        </div>
      </div>
    </div>
  );
}

function ReplyRow({ reply }: { reply: DemoReply }) {
  return (
    <div className="flex gap-2.5">
      <Avatar name={reply.author.name} color={reply.author.avatarColor} size={28} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-1.5 text-[12px]">
          <span className="font-semibold" style={{ color: "#0A0B0D" }}>
            {reply.author.name}
          </span>
          <span style={{ color: "#8A919E" }}>@{reply.author.handle}</span>
          <span style={{ color: "#8A919E" }}>·</span>
          <span style={{ color: "#8A919E" }}>{relativeTime(reply.postedMinutesAgo)}</span>
        </div>
        <p className="mt-1 text-[13px] leading-[1.5]" style={{ color: "#0A0B0D" }}>
          {reply.body}
        </p>
        <VoteStack score={reply.score} />
      </div>
    </div>
  );
}
